import { api, get, post } from '@/lib/api';

const RAIZ = '/cmdb';

export type SourceType = 'ESXI' | 'WAZUH';
export type RunStatus = 'RUNNING' | 'COMPLETED' | 'FAILED' | 'CANCELLED';

export interface DiscoverySource {
  id: string;
  tenantId: string;
  name: string;
  type: SourceType;
  endpoint: string;
  schedule: string;
  enabled: boolean;
  lastRunAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface DiscoveryRun {
  id: string;
  sourceId: string;
  status: RunStatus;
  startedAt: string;
  finishedAt: string | null;
  itemsFound: number;
  itemsCreated: number;
  itemsUpdated: number;
  itemsDrifted: number;
  error: string | null;
}

export interface CiDrift {
  id: string;
  ciId: string;
  runId: string;
  attrKey: string;
  oldValue: string | null;
  newValue: string | null;
  detectedAt: string;
  acknowledgedAt: string | null;
}

export interface CiMergeCandidate {
  id: string;
  ciAId: string;
  ciBId: string;
  confidence: number;
  reason: string;
  status: 'PENDING' | 'MERGED' | 'IGNORED';
}

export interface CreateSourceDto {
  name: string;
  type: SourceType;
  endpoint: string;
  credentials: Record<string, string>;
  schedule?: string;
  enabled?: boolean;
}

export function listarFontes(): Promise<DiscoverySource[]> {
  return get<DiscoverySource[]>(`${RAIZ}/sources`);
}

export function criarFonte(dto: CreateSourceDto): Promise<DiscoverySource> {
  return post<DiscoverySource>(`${RAIZ}/sources`, dto);
}

export function atualizarFonte(id: string, dto: Partial<Omit<CreateSourceDto, 'type'>>): Promise<DiscoverySource> {
  return api<DiscoverySource>(`${RAIZ}/sources/${id}`, { method: 'PATCH', body: dto });
}

export function excluirFonte(id: string): Promise<void> {
  return api<void>(`${RAIZ}/sources/${id}`, { method: 'DELETE' });
}

export function listarRuns(sourceId: string): Promise<DiscoveryRun[]> {
  return get<DiscoveryRun[]>(`${RAIZ}/sources/${sourceId}/runs`);
}

export function iniciarRun(sourceId: string): Promise<{ runId: string }> {
  return post<{ runId: string }>(`${RAIZ}/sources/${sourceId}/runs`, {});
}

export function listarDrifts(): Promise<CiDrift[]> {
  return get<CiDrift[]>(`${RAIZ}/drift`);
}

export function confirmarDrift(id: string): Promise<CiDrift> {
  return post<CiDrift>(`${RAIZ}/drift/${id}/acknowledge`, {});
}

export function listarCandidatos(): Promise<CiMergeCandidate[]> {
  return get<CiMergeCandidate[]>(`${RAIZ}/merge-candidates`);
}

export function resolverCandidato(id: string, action: 'MERGED' | 'IGNORED'): Promise<CiMergeCandidate> {
  return post<CiMergeCandidate>(`${RAIZ}/merge-candidates/${id}/resolve`, { action });
}

export const chavesCmdb = {
  fontes: () => ['cmdb', 'fontes'] as const,
  runs: (sourceId: string) => ['cmdb', 'runs', sourceId] as const,
  drifts: () => ['cmdb', 'drifts'] as const,
  candidatos: () => ['cmdb', 'candidatos'] as const,
};
